/**
 * useFinanceiroCobrancas - Hook CRUD para cobranças financeiras
 * 
 * Operações: listagem, criação (única + parcelada), edição e soft delete
 * Tabelas: financeiro_cobrancas, financeiro_parcelas
 * 
 * @author Sistema
 * @version 1.0.0
 * RELEASE: 2026-03-14 v1.0.0 - Criação do hook (Etapa 1: única + parcelada)
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('FinanceiroCobrancas');

// Tipos de cobrança disponíveis
export const TIPOS_COBRANCA = [
  { value: 'unica', label: 'Única' },
  { value: 'parcelada', label: 'Parcelada' },
  // Etapa 2: { value: 'recorrente', label: 'Recorrente' },
] as const;

export type TipoCobranca = 'unica' | 'parcelada' | 'recorrente';
export type FinanceiroStatus = 'aguardando_emissao' | 'pendente' | 'edicao_andamento' | 'aguardando_pagamento' | 'pago' | 'parcial' | 'vencido' | 'cancelado' | 'processando_cancelamento' | 'falha_cancelamento';

// Interface da cobrança
export interface FinanceiroCobranca {
  id: string;
  tenant_id: string;
  customer_id: string;
  deal_id: string | null;
  tipo_cobranca: TipoCobranca;
  meio_pagamento_id: string | null;
  conta_corrente_id: string | null;
  descricao: string | null;
  valor_total: number;
  moeda: string;
  status: FinanceiroStatus;
  data_emissao: string;
  data_vencimento: string | null;
  observacoes: string | null;
  numero_parcelas: number;
  created_by: string | null;
  provider_id: string | null;
  provider_charge_id: string | null;
  provider_slug: string | null;
  provider_invoice_url: string | null;
  provider_payment_link: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Relacionamentos expandidos (opcional)
  customer?: { id: string; name: string } | null;
  parcelas?: FinanceiroParcela[];
  /** Tipo de pagamento resolvido via join com financeiro_meios_pagamento */
  tipo_pagamento?: string | null;
  /** Nome da conta corrente resolvido via join com financeiro_conta_corrente */
  conta_corrente_nome?: string | null;
  /** Primeira data de pagamento das parcelas (computado client-side) */
  primeira_data_pagamento?: string | null;
  /** Dados do meio de pagamento expandido (join) */
  financeiro_meios_pagamento?: { tipo_pagamento: string } | null;
}

// Interface da parcela
export interface FinanceiroParcela {
  id: string;
  tenant_id: string;
  cobranca_id: string;
  numero_parcela: number;
  valor: number;
  data_vencimento: string;
  data_pagamento: string | null;
  valor_pago: number | null;
  status: FinanceiroStatus;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Interface para criação de cobrança
export interface CobrancaFormData {
  customer_id: string;
  deal_id?: string | null;
  tipo_cobranca: TipoCobranca;
  meio_pagamento_id?: string | null;
  conta_corrente_id?: string | null;
  descricao?: string;
  valor_total: number;
  moeda?: string;
  data_emissao?: string;
  data_vencimento?: string;
  observacoes?: string;
  numero_parcelas?: number;
}

/**
 * Opções adicionais do hook (ex: desabilitar query para uso apenas de mutations)
 */
interface UseFinanceiroCobrancasOptions {
  /** Se false, desabilita a query de listagem (útil quando só precisa de mutations) */
  enabled?: boolean;
}

/**
 * Hook para CRUD de cobranças financeiras
 * 
 * @param filters - Filtros opcionais para a listagem
 * @param options - Opções adicionais (ex: { enabled: false } para desabilitar query)
 */
export function useFinanceiroCobrancas(filters?: {
  customerId?: string;
  dealId?: string;
  status?: FinanceiroStatus;
  tipo?: TipoCobranca;
}, options?: UseFinanceiroCobrancasOptions) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // =========================================================================
  // Realtime: escuta mudanças na tabela financeiro_cobrancas para atualizar
  // automaticamente o status das cobranças (ex: webhook altera status)
  // =========================================================================
  useEffect(() => {
    if (!tenantId) return;

    const channel = supabase
      .channel(`financeiro-cobrancas-rt-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financeiro_cobrancas',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          logger.debug('Realtime: mudança detectada em financeiro_cobrancas', {
            eventType: payload.eventType,
          });
          // Invalida cache para refetch automático
          queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
          queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas-paginated'] });
          // Invalida cache do modal de detalhes (realtime de parcelas/status)
          queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenantId, queryClient]);

  // Listagem de cobranças
  const { data: cobrancas = [], isLoading, error } = useQuery({
    queryKey: ['financeiro-cobrancas', tenantId, filters],
    queryFn: async () => {
      if (!tenantId) return [];

      // SELECT restrito a campos seguros — sem expor provider_charge_id, provider_id, metadata
      let query = supabase
        .from('financeiro_cobrancas')
        .select('id, customer_id, deal_id, tipo_cobranca, meio_pagamento_id, conta_corrente_id, descricao, valor_total, moeda, status, data_emissao, data_vencimento, observacoes, numero_parcelas, created_by, provider_slug, provider_invoice_url, provider_payment_link, created_at, updated_at, financeiro_meios_pagamento!meio_pagamento_id(tipo_pagamento), financeiro_conta_corrente!conta_corrente_id(nome), financeiro_parcelas(data_pagamento)')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      // Filtros opcionais
      if (filters?.customerId) {
        query = query.eq('customer_id', filters.customerId);
      }
      if (filters?.dealId) {
        query = query.eq('deal_id', filters.dealId);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.tipo) {
        query = query.eq('tipo_cobranca', filters.tipo);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Erro ao buscar cobranças', { error: error.message });
        throw error;
      }

      // Mapeia campos dos joins para campos diretos na interface
      return (data as any[]).map((c) => ({
        ...c,
        tipo_pagamento: c.financeiro_meios_pagamento?.tipo_pagamento || null,
        conta_corrente_nome: c.financeiro_conta_corrente?.nome || null,
        primeira_data_pagamento: (c.financeiro_parcelas || [])
          .map((p: any) => p.data_pagamento)
          .filter(Boolean)
          .sort()[0] || null,
      })) as FinanceiroCobranca[];
    },
    // Respeita options.enabled para permitir uso apenas de mutations (sem query pesada)
    enabled: (options?.enabled !== false) && !!tenantId,
  });

  // Buscar parcelas de uma cobrança (campos restritos — sem metadata com provider_payment_id)
  const fetchParcelas = async (cobrancaId: string): Promise<FinanceiroParcela[]> => {
    const { data, error } = await supabase
      .from('financeiro_parcelas')
      .select('id, cobranca_id, numero_parcela, valor, data_vencimento, data_pagamento, valor_pago, status, created_at, updated_at')
      .eq('cobranca_id', cobrancaId)
      .order('numero_parcela');

    if (error) {
      logger.error('Erro ao buscar parcelas', { error: error.message });
      throw error;
    }

    return data as FinanceiroParcela[];
  };

  // Criação de cobrança (com geração automática de parcelas para tipo parcelada)
  const createCobranca = useMutation({
    mutationFn: async (formData: CobrancaFormData) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      // 1. Criar cobrança
      const { data: cobranca, error: cobrancaError } = await supabase
        .from('financeiro_cobrancas')
        .insert({
          tenant_id: tenantId,
          customer_id: formData.customer_id,
          deal_id: formData.deal_id || null,
          tipo_cobranca: formData.tipo_cobranca,
          meio_pagamento_id: formData.meio_pagamento_id || null,
          conta_corrente_id: formData.conta_corrente_id || null,
          descricao: formData.descricao || null,
          valor_total: formData.valor_total,
          moeda: formData.moeda || 'BRL',
          data_emissao: formData.data_emissao || new Date().toISOString().split('T')[0],
          data_vencimento: formData.data_vencimento || null,
          observacoes: formData.observacoes || null,
          numero_parcelas: formData.numero_parcelas || 1,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (cobrancaError) {
        logger.error('Erro ao criar cobrança', { error: cobrancaError.message });
        throw cobrancaError;
      }

      // 2. Gerar parcelas automaticamente para tipo parcelada
      if (formData.tipo_cobranca === 'parcelada' && formData.numero_parcelas && formData.numero_parcelas > 1) {
        const valorParcela = Number((formData.valor_total / formData.numero_parcelas).toFixed(2));
        const dataBase = new Date(formData.data_vencimento || formData.data_emissao || new Date());

        const parcelas = Array.from({ length: formData.numero_parcelas }, (_, i) => {
          const dataVencimento = new Date(dataBase);
          dataVencimento.setMonth(dataVencimento.getMonth() + i);

          return {
            tenant_id: tenantId,
            cobranca_id: cobranca.id,
            numero_parcela: i + 1,
            valor: i === formData.numero_parcelas! - 1
              ? Number((formData.valor_total - valorParcela * (formData.numero_parcelas! - 1)).toFixed(2))
              : valorParcela,
            data_vencimento: dataVencimento.toISOString().split('T')[0],
            status: 'pendente' as const,
          };
        });

        const { error: parcelasError } = await supabase
          .from('financeiro_parcelas')
          .insert(parcelas);

        if (parcelasError) {
          logger.error('Erro ao criar parcelas', { error: parcelasError.message });
          // Não lançar erro aqui pois a cobrança já foi criada
          toast.error('Cobrança criada, mas houve erro ao gerar parcelas');
        }
      }

      return cobranca;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      toast.success('Cobrança criada com sucesso');
    },

    onError: (error: Error) => {
      toast.error(`Erro ao criar cobrança: ${error.message}`);
    },
  });

  // Soft delete de cobrança via RPC SECURITY DEFINER
  // Contorna RLS (deleted_at IS NULL no SELECT) e valida permissões granulares
  const deleteCobranca = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      const { error } = await supabase.rpc('soft_delete_financeiro_cobranca', {
        p_cobranca_id: id,
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao excluir cobrança via RPC', { error: error.message });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
      toast.success('Cobrança excluída com sucesso');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir cobrança: ${error.message}`);
    },
  });

  return {
    cobrancas,
    isLoading,
    error,
    createCobranca,
    deleteCobranca,
    fetchParcelas,
  };
}
